import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ClipboardList, Plus, ShieldAlert, TriangleAlert } from 'lucide-react';
import {
  inspectionsApi,
  InspectionSummary,
  InspectionStatus,
} from '@/api/inspections';
import NewInspectionModal from '@/components/NewInspectionModal';
import {
  Button,
  EmptyState,
  Skeleton,
  Tabs,
  cn,
} from '@/ui';
import type { TabItem } from '@/ui';

const STATUS_CHIP: Record<InspectionStatus, { label: string; className: string; icon?: React.ReactNode }> = {
  pending_agreement: {
    label: 'Awaiting Agreement',
    className: 'bg-[var(--warning-50,#fffbeb)] text-[var(--warning-700,#92400e)] border border-[var(--warning-200,#fde68a)]',
    icon: <ClipboardList className="h-3 w-3 shrink-0" aria-hidden />,
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-[var(--brand-50)] text-[var(--brand-700)] border border-[var(--brand-200)]',
  },
  completed: {
    label: 'Completed',
    className: 'bg-[var(--brand-500)] text-white border border-[var(--brand-500)]',
    icon: <Check className="h-3 w-3 shrink-0" aria-hidden />,
  },
  archived: {
    label: 'Archived',
    className: 'bg-[var(--ink-100)] text-[var(--fg-3)] border border-transparent',
  },
};

type FilterId = 'all' | 'pending_agreement' | 'in_progress' | 'completed' | 'archived';

const FILTERS: Array<{ id: FilterId; label: string; match: (s: InspectionStatus) => boolean }> = [
  { id: 'all',               label: 'All',              match: () => true },
  { id: 'pending_agreement', label: 'Awaiting agreement', match: (s) => s === 'pending_agreement' },
  { id: 'in_progress',       label: 'In progress',      match: (s) => s === 'in_progress' },
  { id: 'completed',         label: 'Completed',        match: (s) => s === 'completed' },
  { id: 'archived',          label: 'Archived',         match: (s) => s === 'archived' },
];

const InspectionList: React.FC = () => {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<InspectionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<FilterId>('all');

  useEffect(() => {
    const load = async () => {
      try {
        const result = await inspectionsApi.list();
        if (result.success && result.data) {
          setInspections(result.data);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCreated = (inspection: InspectionSummary) => {
    setShowModal(false);
    navigate(`/inspections/${inspection.id}`);
  };

  const statusCounts = useMemo(() => {
    const counts: Record<FilterId, number> = {
      all: inspections.length,
      pending_agreement: 0,
      in_progress: 0,
      completed: 0,
      archived: 0,
    };
    for (const i of inspections) {
      if (i.status === 'pending_agreement') counts.pending_agreement += 1;
      else if (i.status === 'in_progress') counts.in_progress += 1;
      else if (i.status === 'completed') counts.completed += 1;
      else if (i.status === 'archived') counts.archived += 1;
    }
    return counts;
  }, [inspections]);

  const filtered = useMemo(() => {
    const pred = FILTERS.find((f) => f.id === filter)?.match ?? (() => true);
    return inspections.filter((i) => pred(i.status));
  }, [inspections, filter]);

  const tabItems: TabItem[] = FILTERS.map((f) => {
    const count = statusCounts[f.id];
    return {
      id: f.id,
      label: f.label,
      // Only render a count pill when the bucket has items — an empty pill
      // adds chrome without signal.
      ...(count > 0 ? { count } : {}),
      countTone: f.id === 'in_progress' ? 'brand' : f.id === 'pending_agreement' ? 'warning' : 'neutral',
    };
  });

  return (
    <div>
      {/* Page head — serif display + metadata subline, per the design README:
          "the document, not the dashboard". */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <h1 className="font-display text-display-sm text-ink leading-tight">
            Inspections
          </h1>
          <p className="mt-1 text-body-sm text-ink-muted">
            {inspections.length === 0
              ? 'Start a new inspection to begin capturing findings.'
              : `${inspections.length} ${inspections.length === 1 ? 'inspection' : 'inspections'} · ${statusCounts.in_progress + statusCounts.pending_agreement} active`}
          </p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          leadingIcon={<Plus className="h-4 w-4" />}
          className="shrink-0"
        >
          New Inspection
        </Button>
      </div>

      {/* Filter tabs — hidden when empty so we don't render a lonely row of zeros.
          The horizontal-scroll wrapper keeps the segmented control on a single
          row when "All / In progress / Completed / Archived" + count badges
          exceed the iPhone portrait width — without it, the inline-flex parent
          stays its content width and the page itself scrolls horizontally. */}
      {inspections.length > 0 && (
        <div className="mb-4 -mx-4 px-4 overflow-x-auto scrollbar-none sm:mx-0 sm:px-0 sm:overflow-x-visible">
          <Tabs
            items={tabItems}
            activeId={filter}
            onChange={(id) => setFilter(id as FilterId)}
            variant="segmented"
            label="Filter inspections by status"
          />
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <InspectionCardSkeleton key={i} />
          ))}
        </div>
      ) : inspections.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="h-10 w-10" />}
          title="No inspections yet"
          description="Start a new one when you're on-site — sections, photos, and findings roll up into a report automatically."
          action={
            <Button onClick={() => setShowModal(true)} leadingIcon={<Plus className="h-4 w-4" />}>
              Start First Inspection
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="h-10 w-10" />}
          title={`No ${FILTERS.find((f) => f.id === filter)?.label.toLowerCase() ?? ''} inspections`}
          description="Try switching to another filter or start a new inspection."
          action={
            <Button variant="secondary" onClick={() => setFilter('all')}>
              Show all
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((inspection) => (
            <InspectionCard
              key={inspection.id}
              inspection={inspection}
              onOpen={() => navigate(`/inspections/${inspection.id}`)}
            />
          ))}
        </div>
      )}

      {showModal && (
        <NewInspectionModal onCreated={handleCreated} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
};

export default InspectionList;

// ──────────────────────────────────────────────────────────────────────────
// Card
// ──────────────────────────────────────────────────────────────────────────

interface InspectionCardProps {
  inspection: InspectionSummary;
  onOpen: () => void;
}

const InspectionCard: React.FC<InspectionCardProps> = ({ inspection, onOpen }) => {
  const statusCfg = STATUS_CHIP[inspection.status];
  const isActive = inspection.status === 'in_progress' || inspection.status === 'pending_agreement';

  // The API may omit the severity split on older builds — treat absence as 0
  // so the UI degrades gracefully (total count still renders). pg returns
  // COUNT(*) as a bigint string, so coerce through Number() rather than
  // trusting the raw shape.
  const safety = Number(inspection.safetyCount ?? 0);
  const recommendations = Number(inspection.recommendationCount ?? 0);

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        // Card treatment mirrors the design kit: pure white on paper, 1px
        // hairline, minimal shadow, hover lifts only the border/shadow (no
        // scale, no bloom — "we're a document, not a dashboard").
        'group relative w-full text-left overflow-hidden',
        'bg-surface-1 border border-border-subtle rounded-xl shadow-xs',
        'hover:border-brand/40 hover:shadow-card transition-all duration-180 ease-premium',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
      )}
    >
      {/* Active-state accent bar — signals at a glance which inspections
          are still on the inspector's plate. */}
      {isActive && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-1 bg-brand"
        />
      )}

      <div className={cn('flex items-start gap-4 p-4', isActive && 'pl-5')}>
        <div className="flex-1 min-w-0">
          <p className="font-display text-heading-md text-ink truncate">
            {inspection.propertyAddress}
          </p>
          <p className="mt-0.5 text-body-sm text-ink-muted truncate">
            {/* Metadata rail: client · inspection date. Middle dot separator
                and en/em dashes per the brand guide. */}
            {inspection.clientName ? (
              <>
                <span className="text-ink">{inspection.clientName}</span>
                <span className="mx-1.5 text-ink-faint" aria-hidden>·</span>
              </>
            ) : null}
            <span className="nums-tabular">{formatDate(inspection.inspectionDate)}</span>
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-caption font-medium whitespace-nowrap',
            statusCfg.className,
          )}>
            {statusCfg.icon}
            {statusCfg.label}
          </span>

          {/* Severity split — scannable 2-value view matches the client-facing
              severity model. Falls back to a neutral "N findings" count when
              the split is zero (still passes existing tests). */}
          {safety + recommendations > 0 ? (
            <div className="flex items-center gap-1.5">
              {safety > 0 && (
                <SeverityCountPill
                  tone="safety"
                  count={safety}
                  icon={<ShieldAlert className="h-3 w-3" aria-hidden />}
                  ariaLabel={`${safety} safety ${safety === 1 ? 'concern' : 'concerns'}`}
                />
              )}
              {recommendations > 0 && (
                <SeverityCountPill
                  tone="recommend"
                  count={recommendations}
                  icon={<TriangleAlert className="h-3 w-3" aria-hidden />}
                  ariaLabel={`${recommendations} ${recommendations === 1 ? 'recommendation' : 'recommendations'}`}
                />
              )}
              <span className="sr-only">
                {inspection.findingCount}{' '}
                {inspection.findingCount === 1 ? 'finding' : 'findings'}
              </span>
            </div>
          ) : (
            <span className="text-caption text-ink-faint nums-tabular">
              {inspection.findingCount}{' '}
              {inspection.findingCount === 1 ? 'finding' : 'findings'}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Small primitives used only by the list card
// ──────────────────────────────────────────────────────────────────────────

interface SeverityCountPillProps {
  tone: 'safety' | 'recommend';
  count: number;
  icon: React.ReactNode;
  ariaLabel: string;
}

const SeverityCountPill: React.FC<SeverityCountPillProps> = ({
  tone, count, icon, ariaLabel,
}) => (
  <span
    aria-label={ariaLabel}
    className={cn(
      'inline-flex items-center gap-1 px-1.5 h-5 rounded-full text-[11px] font-semibold tabular-nums border',
      tone === 'safety'
        ? 'bg-sev-critical-soft text-sev-critical-ink border-sev-critical/20'
        : 'bg-sev-minor-soft text-sev-minor-ink border-sev-minor/25',
    )}
  >
    {icon}
    {count}
  </span>
);

const InspectionCardSkeleton: React.FC = () => (
  <div className="card p-4 flex items-start gap-4">
    <div className="flex-1 min-w-0 space-y-2">
      <Skeleton width="w-3/4" height="h-4" />
      <Skeleton width="w-1/2" height="h-3" />
    </div>
    <div className="flex flex-col items-end gap-2 shrink-0">
      <Skeleton width="w-20" height="h-5" shape="pill" />
      <Skeleton width="w-16" height="h-5" shape="pill" />
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString('en-US', opts);
}
